import { Box, Card, Stack, Typography } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import DataGridCustom from 'src/components/data-grid-custom';
import { paths } from 'src/routes/paths';
import type { BenefitRow } from '../../benefits-list/benefits-list.hook';
import { BenefitsTableToolbar } from '../../benefits-list/components/benefits-table-toolbar';
import ThemeBenefitsDrawerFilters from './theme-benefits-drawer-filters';
import useThemeBenefitsList from './theme-benefits-list.hook';

type Props = {
  fixedFilter: 'theme' | 'category';
};

export default function ProductsListByTheme({ fixedFilter }: Props) {
  const { id = '' } = useParams();
  const isTheme = fixedFilter === 'theme';
  const hook = useThemeBenefitsList({
    entityId: id,
    entityLabel: isTheme ? 'tema' : 'categoria',
    fixedFilterField: isTheme ? 'themesId' : 'categoriesId',
  });

  const handleRowClick = useCallback((params: GridRowParams<BenefitRow>) => {
    const benefitId = String(params.row.benefitId || params.id || '');

    if (!benefitId) {
      return;
    }

    window.open(
      `${paths.dashboard.benefitsDetailsEdit(benefitId)}?benefitType=${params.row.benefitType}`,
      '_blank'
    );
  }, []);

  const table = useMemo(
    () => (
      <DataGridCustom
        animatedEachRow
        checkboxSelection={false}
        customFilters={
          <BenefitsTableToolbar
            searchText={hook.searchText}
            selectedFiltersCount={hook.selectedFiltersCount}
            handleClickFilters={() => hook.setOpenFiltersDrawer(true)}
            setSearchText={hook.handleSearchTextChange}
          />
        }
        excludeExportButton
        columns={hook.columns}
        rows={hook.rows}
        totalRowsCount={hook.benefitsList.totalFiltered ?? hook.benefitsList.total}
        filteredRowsCount={hook.benefitsList.totalFiltered ?? hook.benefitsList.total}
        initialVisibleColumns={{}}
        sortModel={hook.gridSortModel}
        paginationModel={hook.paginationModel}
        pageSizeOptions={[5, 10, 20, 30, 40, 50]}
        isLoading={hook.isLoading}
        onRowClick={handleRowClick}
        setSortModel={hook.handleSortModelChange}
        setPaginationModel={hook.setPaginationModel}
        height={550}
      />
    ),
    [handleRowClick, hook]
  );

  return (
    <>
      <Stack pt={8}>
        <Typography variant="h6" fontWeight="bold">
          Benefícios Associados {isTheme ? 'ao Tema' : 'à Categoria'}
        </Typography>
      </Stack>
      <Card sx={{ px: 2, pt: 1, pb: 0, boxShadow: 4 }}>
        <Box height="100%">{table}</Box>
      </Card>

      <ThemeBenefitsDrawerFilters
        fixedFilter={fixedFilter}
        isOpen={hook.openFiltersDrawer}
        onClose={() => hook.setOpenFiltersDrawer(false)}
        onApplyFilters={hook.onApplyFilters}
        appliedFilters={hook.appliedDraftFilters}
      />
    </>
  );
}
